import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, Loader2, MessageSquare, Play, Repeat, Sparkles, Users, Workflow } from 'lucide-react';
import { AppChrome, toolLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';
type PrimitiveId = 'chats' | 'shared' | 'workflows' | 'pulses' | 'skills';
const PRIMITIVES: Array<{
  id: PrimitiveId;
  label: string;
  icon: React.ElementType;
  copy: string;
}> = [{
  id: 'chats',
  label: 'AI chats',
  icon: MessageSquare,
  copy: 'A familiar chat interface, except it is signed into your tools. Enough to replace most standalone ChatGPT use.'
}, {
  id: 'shared',
  label: 'Team workspaces',
  icon: Users,
  copy: 'Tracks and groups keep work, files, and access together, so a project has one place instead of living in personal accounts.'
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  copy: 'Turn a process you repeat into defined steps with declared inputs, then run it on demand and see every run.'
}, {
  id: 'pulses',
  label: 'Pulses',
  icon: Repeat,
  copy: 'Work that runs without being asked — on a schedule, reporting into the channel that needs it.'
}, {
  id: 'skills',
  label: 'Skills',
  icon: Sparkles,
  copy: 'Show Giga how your company does something once, and it reuses that method everywhere else.'
}];

/**
 * One row treatment for every list in these screens. A faint warm fill plus a
 * 1px contact shadow gives each row a surface, which is the difference between
 * a product screenshot and an outlined box.
 */
const ROW = 'rounded-xl border border-[#EFEAE1] bg-[#FCFAF6] shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_1px_2px_-1px_rgba(43,33,26,0.06)]';
export function PlatformPillar() {
  const [active, setActive] = useState<PrimitiveId>('chats');
  return <div data-id="element-361" data-mp="e1g1p72v4bjp">
      <SectionIntro className="mx-auto max-w-3xl text-center" data-id="element-362" data-mp="e16axh9zu0cy">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-363" data-mp="92uz51051fs3">
          Workspace
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-364" data-mp="yrimlseawytm">
          One place to manage
          <br data-id="element-365" data-mp="12svpbtm3klwb" />
          AI within your company
        </h2>
        <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-366" data-mp="17rfb9u0oxxoo">
          Let your power users share their skills with other team members. Build automations in plain English. Schedule them with Pulses. Everything managed in one place, that you own, in the cloud.
        </p>
      </SectionIntro>

      <div className="mt-12 grid items-start gap-8 md:mt-16 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-12" data-id="element-367" data-mp="1lq58d9nifxd1">
        {/* Primitives as a list you step through: only the active one carries
         its explanation, so five ideas never shout at once. */}
        <ul className="space-y-1" data-id="element-368" data-mp="20gw24n8nx6is">
          {PRIMITIVES.map(p => {
          const on = active === p.id;
          return <li key={p.id} data-id="element-369" data-mp="25enwdnws54q5">
                <button type="button" onClick={() => setActive(p.id)} aria-current={on ? 'true' : undefined} className={`w-full rounded-2xl px-4 py-3.5 text-left transition-colors ${on ? 'bg-white shadow-[0_18px_44px_-30px_rgba(43,33,26,0.5)] ring-1 ring-[#E7E0D4]' : 'hover:bg-white/60'}`} data-id="element-370" data-mp="25fyzrmmbcagq">
                  <span className="flex items-center gap-2.5" data-id="element-371" data-mp="3fztyfv3adphw">
                    <p.icon size={17} className={on ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-372" data-mp="3b0qw4ieoxxdm" />
                    <span className={`font-title text-xl font-bold tracking-tight ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-373" data-mp="cj4p61cbk1oj">
                      {p.label}
                    </span>
                  </span>
                  {on && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-2 pl-[27px] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-374" data-mp="2rqm5jl5sjqn">
                      {p.copy}
                    </motion.p>}
                </button>
              </li>;
        })}
        </ul>

        <AppChrome active={active === 'pulses' ? 'Pulses' : active === 'shared' ? 'Groups' : undefined} data-id="element-375" data-mp="fzpswnup1tei">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} className="px-5 py-5 md:px-7 md:py-6" data-id="element-376" data-mp="12ysqcd74dnu">
            {active === 'chats' && <ChatsScreen data-id="element-377" data-mp="yow31mhf9cop" />}
            {active === 'shared' && <SharedScreen data-id="element-378" data-mp="yrs2jf8x49f9" />}
            {active === 'workflows' && <WorkflowsScreen data-id="element-379" data-mp="k09p9p7g0szl" />}
            {active === 'pulses' && <PulsesScreen data-id="element-380" data-mp="p04ezpfhfcj2" />}
            {active === 'skills' && <SkillsScreen data-id="element-381" data-mp="oyjku0x2xvlq" />}
          </motion.div>
        </AppChrome>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta
}: {
  title: string;
  meta: string;
}) {
  // A real app view states what you're looking at and then rules off. The
  // hairline is what stops these reading as floating wireframe blocks.
  return <div className="mb-5 border-b border-[#F1ECE3] pb-3.5" data-id="element-382" data-mp="324re6v0zahf8">
      <h3 className="font-title text-[21px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-383" data-mp="2x6y6fxk11fcu">
        {title}
      </h3>
      <p className="mt-1 text-[12.5px] tabular-nums text-neutral-500" data-id="element-384" data-mp="3byqdlm9o3n3h">{meta}</p>
    </div>;
}
function ChatsScreen() {
  return <div data-id="element-385" data-mp="2j7q3quonnp3w">
      <ScreenHead title="Renewal risk in Salesforce" meta="Giga Master · 3 sources" data-id="element-386" data-mp="2j7gej9bfplz9" />
      <div className="flex justify-end" data-id="element-387" data-mp="2e952qt1kx1ee">
        <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A] shadow-[0_1px_2px_-1px_rgba(43,33,26,0.12)]" data-id="element-388" data-mp="2ea6pvvvvdech">
          Which renewals due this quarter look at risk?
        </p>
      </div>
      {/* The work Giga did, held together by a rail so it reads as a trace
       rather than three loose lines. */}
      <div className="relative mt-4 space-y-2 pl-3.5 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[2px] before:rounded-full before:bg-[#EFEAE1] before:content-['']" data-id="element-389" data-mp="3hde392apmgy">
        {[['salesforce', 'Read 34 opportunities closing this quarter'], ['intercom', 'Checked support volume per account'], ['gmail', 'Scanned the last 60 days of thread activity']].map(([slug, label]) => <p key={label} className="flex items-center gap-2 text-[12.5px] text-neutral-500" data-id="element-390" data-mp="3aatoz9qx5y52">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-md border border-[#EFEAE1] bg-white" data-id="element-391" data-mp="2dl2vndud6h0u">
              <img src={toolLogo(slug)} alt="" aria-hidden="true" className="size-3 object-contain" data-id="element-392" data-mp="2ij4h25f9isfh" />
            </span>
            {label}
            <Check size={12} strokeWidth={3} className="text-emerald-500" data-id="element-393" data-mp="2mk7qoa6pvs89" />
          </p>)}
      </div>
      <div className="mt-4 space-y-3 text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-394" data-mp="2rdius3k59uwh">
        <p data-id="element-395" data-mp="1uttkl04y3k75">
          <strong className="font-semibold text-[#2B211A]" data-id="element-396" data-mp="1uu33k3rkor3c">
            Three of eleven look at risk.
          </strong>{' '}
          Northwind has had no exec contact in 41 days and two unresolved
          tickets. Meridian's champion left in July. Fairline's usage is down
          38% month over month.
        </p>
        <p className="text-neutral-500" data-id="element-397" data-mp="1ibpind8o6iks">
          I've drafted outreach for each owner — want me to send it?
        </p>
      </div>
      <div className="mt-5 flex flex-wrap gap-1.5 border-t border-[#EFEAE1] pt-3.5" data-id="element-398" data-mp="1ddfl4vzh35w0">
        {['34 opportunities', '12 tickets', '60 days of mail'].map(s => <span key={s} className="rounded-full border border-[#EFEAE1] bg-[#FCFAF6] px-2.5 py-1 text-[11.5px] font-medium tabular-nums text-neutral-500" data-id="element-399" data-mp="18fmj8f57cy1j">
            {s}
          </span>)}
      </div>
    </div>;
}
function SharedScreen() {
  const groups = [{
    name: 'Giga Master',
    initials: 'GM',
    color: '#134E4A',
    members: 18,
    chats: 214,
    tracks: 33
  }, {
    name: 'Giga Marketing',
    initials: 'GM',
    color: '#7C2D12',
    members: 6,
    chats: 88,
    tracks: 12
  }, {
    name: 'NoContact Ops',
    initials: 'NO',
    color: '#1E3A5F',
    members: 4,
    chats: 51,
    tracks: 9
  }, {
    name: 'Everyone',
    initials: 'EV',
    color: '#3F3A2E',
    members: 18,
    chats: 106,
    tracks: 4
  }];
  return <div data-id="element-400" data-mp="1zkruqa2mm5vm">
      <ScreenHead title="Groups" meta="Access follows the group, not the item" data-id="element-401" data-mp="1zix9svrcudrj" />
      <ul className="space-y-2" data-id="element-402" data-mp="24ijutrdfcyaw">
        {groups.map(g => <li key={g.name} className={`flex items-center gap-3 px-3.5 py-3 ${ROW}`} data-id="element-403" data-mp="24h8l7axaswbv">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-xl text-[10.5px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" style={{
          backgroundColor: g.color
        }} data-id="element-404" data-mp="1psm69abl26l6">
              {g.initials}
            </span>
            <span className="min-w-0 flex-1" data-id="element-405" data-mp="1kvc6p1uhcejm">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-406" data-mp="16urmc18x2ao0">
                {g.name}
              </span>
              <span className="font-mono text-[11px] text-neutral-400" data-id="element-407" data-mp="1bt2zakuid1ag">
                spaces/{g.name.toLowerCase().replace(/\s+/g, '-')}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-408" data-mp="naazzjsvn1ka">
              {[[g.members, 'members'], [g.chats, 'chats'], [g.tracks, 'tracks']].map(([n, l]) => <span key={String(l)} title={String(l)} className="min-w-[30px] rounded-lg border border-[#EFEAE1] bg-white px-2 py-1 text-center text-[11px] font-semibold tabular-nums text-neutral-500" data-id="element-409" data-mp="124pb6y6796oe">
                  {n}
                </span>)}
            </span>
          </li>)}
      </ul>
    </div>;
}
function WorkflowsScreen() {
  const steps = ['Pull every deal that closed last week from Salesforce', 'Match each to its support and usage history', 'Write the win/loss summary into the Notion database', 'Post the digest to #revenue'];
  return <div data-id="element-410" data-mp="1tx6izpihkwa2">
      <ScreenHead title="Weekly win/loss digest" meta="4 steps · 26 runs · 96% success" data-id="element-411" data-mp="1tyzm4utw4ydb" />
      <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-412" data-mp="1px5tn9tun0o8">
        {[['Reporting period', 'Date · required'], ['Notion database', 'Database · required'], ['Owner', 'Text · optional']].map(([k, v]) => <span key={k} className={`px-2.5 py-1.5 ${ROW}`} data-id="element-413" data-mp="3ag6gaecj9os6">
            <span className="block text-[12px] font-semibold text-[#2B211A]" data-id="element-414" data-mp="3aelp8ypfgnz2">
              {k}
            </span>
            <span className="text-[10.5px] text-neutral-400" data-id="element-415" data-mp="30iin3sukspc4">{v}</span>
          </span>)}
      </div>
      {/* A rail behind the numbers makes the steps read as one sequence
       rather than four unrelated cards. */}
      <ol className="relative space-y-2 before:absolute before:bottom-5 before:left-[27px] before:top-5 before:w-px before:bg-[#EFEAE1] before:content-['']" data-id="element-416" data-mp="3t6o7gehpwcbf">
        {steps.map((s, i) => <li key={s} className={`relative flex gap-3 px-3.5 py-2.5 ${ROW}`} data-id="element-417" data-mp="3k6hy94t1xyi2" data-mpi={i}>
            <span className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-[#2B211A] text-[10.5px] font-bold tabular-nums text-white shadow-[0_1px_2px_rgba(43,33,26,0.3)]" data-id="element-418" data-mp="3k8klxi6qffjm">
              {i + 1}
            </span>
            <span className="text-[13px] leading-snug text-[#3D3229]" data-id="element-419" data-mp="o9tucg83o94k">{s}</span>
          </li>)}
      </ol>
      <div className="mt-4 flex items-center gap-2 border-t border-[#EFEAE1] pt-3.5" data-id="element-420" data-mp="sadrvkwl5er8">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3 py-1.5 text-[12px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_6px_14px_-8px_rgba(43,33,26,0.7)]" data-id="element-421" data-mp="xa8gfhsw1s94">
          <Play size={11} fill="currentColor" data-id="element-422" data-mp="1701q2d9gzxaq" />
          Run now
        </span>
        <span className="text-[12px] tabular-nums text-neutral-500" data-id="element-423" data-mp="1byuw5kmfgm0v">
          Last run Monday 09:00 · 1m 12s
        </span>
      </div>
    </div>;
}
function PulsesScreen() {
  const pulses = [{
    name: 'Monday revenue brief',
    cadence: 'Mondays, 08:00',
    next: 'in 2 days',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Ad spend anomaly check',
    cadence: 'Daily, 07:30',
    next: 'in 14 hours',
    health: [1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2]
  }, {
    name: 'Support escalation sweep',
    cadence: 'Every 4 hours',
    next: 'in 51 minutes',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Churn signal digest',
    cadence: 'Fridays, 16:00',
    next: 'in 6 days',
    health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
  }];
  return <div data-id="element-424" data-mp="12v15xqzdrkb9">
      <ScreenHead title="Pulses" meta="4 enabled · work that runs without being asked" data-id="element-425" data-mp="12urf616ih94i" />
      <ul className="space-y-2" data-id="element-426" data-mp="y05cqnieqqwk">
        {pulses.map(p => <li key={p.name} className={`flex flex-wrap items-center gap-3 px-3.5 py-3 ${ROW}`} data-id="element-427" data-mp="1cuibm8emty78">
            <span className="min-w-0 flex-1" data-id="element-428" data-mp="17x07cvbk875k">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-429" data-mp="17u4c5er3jwkt">
                {p.name}
              </span>
              <span className="text-[11.5px] text-neutral-500" data-id="element-430" data-mp="1hli513ojly4z">
                {p.cadence}
              </span>
            </span>
            {/* Twelve ticks say "is this healthy" faster than a percentage. */}
            <span className="flex shrink-0 items-end gap-[3px] rounded-md bg-white px-1.5 py-1 ring-1 ring-[#F1ECE3]" data-id="element-431" data-mp="1qnhoe2n15eb3">
              {p.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${h === 1 ? 'bg-emerald-500/80' : h === 0 ? 'bg-red-500/85' : 'bg-amber-500/85'}`} data-id="element-432" data-mp="2a99tpr5361qi" data-mpi={i} />)}
            </span>
            <span className="flex w-[92px] shrink-0 items-center justify-end gap-1.5 text-[11.5px] font-medium tabular-nums text-neutral-500" data-id="element-433" data-mp="209iujc5kyyk3">
              <Clock size={11} className="text-neutral-300" data-id="element-434" data-mp="25b89v3zu5ydz" />
              {p.next}
            </span>
          </li>)}
      </ul>
      <div className="mt-3 flex items-center gap-2 rounded-xl border border-[#EA580C]/20 bg-[#FFF8F3] px-3.5 py-2.5 text-[12.5px] text-[#6B5D52]" data-id="element-435" data-mp="1w98ro8d34o9g">
        <Loader2 size={12} className="animate-spin text-[#EA580C]" data-id="element-436" data-mp="1hlnn0a0kagzf" />
        Support escalation sweep is running — step 2 of 4
      </div>
    </div>;
}
function SkillsScreen() {
  const skills = [{
    name: 'How we qualify an inbound lead',
    used: 142,
    group: 'Giga Marketing'
  }, {
    name: 'Our weekly reporting format',
    used: 96,
    group: 'Giga Master'
  }, {
    name: 'Escalating a P1 incident',
    used: 38,
    group: 'NoContact Ops'
  }, {
    name: 'Drafting a renewal email',
    used: 211,
    group: 'Giga Master'
  }];
  return <div data-id="element-437" data-mp="vr8bv3a7dz9p">
      <ScreenHead title="Skills" meta="Recorded once, reused everywhere" data-id="element-438" data-mp="vqopnadisopv" />
      <ul className="space-y-2" data-id="element-439" data-mp="qswwkd152wk3">
        {skills.map(s => <li key={s.name} className={`flex items-center gap-3 px-3.5 py-3 ${ROW}`} data-id="element-440" data-mp="qs3xly8f9bci">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-[#FFF1E8] ring-1 ring-[#EA580C]/15" data-id="element-441" data-mp="mrti9uyqp8kz">
              <Sparkles size={14} className="text-[#EA580C]" data-id="element-442" data-mp="hu1th9te62l0" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-443" data-mp="cte0iillrbo5">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-444" data-mp="7u169csej2bj">
                {s.name}
              </span>
              <span className="text-[11.5px] text-neutral-500" data-id="element-445" data-mp="275lu9sm6tmic">{s.group}</span>
            </span>
            <span className="shrink-0 rounded-md border border-[#EFEAE1] bg-white px-2 py-1 text-[11px] font-semibold tabular-nums text-neutral-500" data-id="element-446" data-mp="2293s90i8s29p">
              used {s.used}×
            </span>
          </li>)}
      </ul>
      <p className="mt-3 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-447" data-mp="2eolcvw1tra0n">
        Every chat, workflow, and pulse in the workspace draws on these, so the
        method stays consistent no matter who asks.
      </p>
    </div>;
}